import { gql } from '@apollo/client';
import type { ReachPost } from './reach';
import type { BarState } from './campaignColors';
import { andList } from '../cms/pageChecks';
import { POST_STATUS, TO_APPROVE } from '../statusWords';

/**
 * The campaign board (TinyCMS UX optimization review, 2026-10-23): a dated
 * campaign as containers — the website, one per connected channel, other
 * work — of cards, each with a due date (when it should be READY, not when it
 * goes out), notes, and the details still missing. The apiserver builds the
 * cards (reach/services/campaignBoard.js); this file words them.
 */

export const CAMPAIGN_BOARD = gql`
  query CampaignBoard($campaignId: ID!) {
    campaignBoard(campaignId: $campaignId)
  }
`;

/** The campaigns this person may open: all dated ones, or a Campaign-role person's own. */
export const CAMPAIGN_LIST = gql`
  query CampaignList($accountId: ID!) {
    campaignList(accountId: $accountId)
  }
`;

export const CAMPAIGN_DUE_WORK = gql`
  query CampaignDueWork($accountId: ID!) {
    campaignDueWork(accountId: $accountId)
  }
`;

export const SAVE_CAMPAIGN_WORK = gql`
  mutation SaveCampaignWork(
    $subjectKind: String!
    $subjectId: ID!
    $note: String
    $dueDate: String
    $done: Boolean
    $link: JSON
  ) {
    saveCampaignWork(
      subjectKind: $subjectKind
      subjectId: $subjectId
      note: $note
      dueDate: $dueDate
      done: $done
      link: $link
    )
  }
`;

/** The named links at the top of a campaign's board (replaces the list). */
export const SAVE_CAMPAIGN_LINKS = gql`
  mutation SaveCampaignLinks($campaignId: ID!, $links: JSON!) {
    saveCampaignLinks(campaignId: $campaignId, links: $links)
  }
`;

export const CREATE_CAMPAIGN_ITEM = gql`
  mutation CreateCampaignItem($campaignId: ID!, $kind: String!, $title: String, $dueDate: String) {
    createCampaignItem(campaignId: $campaignId, kind: $kind, title: $title, dueDate: $dueDate)
  }
`;

export const SAVE_CAMPAIGN_TASK = gql`
  mutation SaveCampaignTask($id: ID!, $title: String, $done: Boolean, $dueDate: String) {
    saveCampaignTask(id: $id, title: $title, done: $done, dueDate: $dueDate)
  }
`;

export const SAVE_CAMPAIGN_ARTICLE = gql`
  mutation SaveCampaignArticle($id: ID!, $input: JSON!) {
    saveCampaignArticle(id: $id, input: $input)
  }
`;

export const SAVE_CAMPAIGN_LANDING = gql`
  mutation SaveCampaignLanding($campaignId: ID!, $input: JSON!) {
    saveCampaignLanding(campaignId: $campaignId, input: $input)
  }
`;

export const INVITE_TO_CAMPAIGN = gql`
  mutation InviteToCampaign($campaignId: ID!, $email: String!) {
    inviteToCampaign(campaignId: $campaignId, email: $email)
  }
`;

export const REMOVE_FROM_CAMPAIGN = gql`
  mutation RemoveFromCampaign($campaignId: ID!, $userId: ID!) {
    removeFromCampaign(campaignId: $campaignId, userId: $userId)
  }
`;

export interface BoardField {
  label: string;
  ok: boolean;
}

export type CardType = 'post' | 'plan' | 'article' | 'task' | 'landing' | 'form';

/**
 * A link to material outside tinycms (backlog 90258646): a campaign's named
 * links at the top of its board, or one card's own file or doc (label optional).
 */
export interface CampaignLink {
  url: string;
  label: string;
}

export const MAX_CAMPAIGN_LINKS = 10;

/** A full http(s) address, or null. */
export function cleanLinkUrl(raw: string): string | null {
  const v = raw.trim();
  if (!v) return null;
  try {
    const u = new URL(/^[a-z][a-z0-9+.-]*:/i.test(v) ? v : `https://${v}`);
    return u.protocol === 'https:' || u.protocol === 'http:' ? u.toString() : null;
  } catch {
    return null;
  }
}

/** What a link is called: its label, else its host ("drive.google.com"). */
export function linkName(link: CampaignLink): string {
  if (link.label.trim()) return link.label.trim();
  try {
    return new URL(link.url).host.replace(/^www\./, '');
  } catch {
    return link.url;
  }
}

export interface BoardCard {
  key: string;
  subject: 'item' | 'post' | 'plan' | 'landing' | 'form';
  id: string;
  type: CardType;
  kind: string;
  text: string;
  untitled: boolean;
  fields: BoardField[];
  status: string;
  done: boolean;
  note: string;
  link?: CampaignLink | null;
  /** A post that's scheduled or going out: ready in time, never overdue (backlog b35b62cf). */
  dueMet?: boolean;
  dueDate: string | null;
  dueBase: string | null;
  dueOffset: number;
  post?: ReachPost;
  plan?: { title: string; content: string; scheduledDate: string | null };
  topicId?: string | null;
  pageId?: string;
  formId?: string;
  article?: { idea: string; title: string; summary: string; body: string; heroUrl: string | null };
  landing?: {
    /** existing: a page the campaign didn't make; url: another site (both "Not tracked"). */
    kind: 'page' | 'agent' | 'url' | 'existing';
    url: string | null;
    path?: string;
    title?: string;
    headline?: string;
    copy?: string;
    heroUrl?: string | null;
    hasHero?: boolean;
    live?: boolean;
    formName?: string | null;
  };
  form?: {
    name: string;
    fields: Array<{ label: string; required: boolean }>;
    successMessage: string;
  };
}

export interface BoardContainer {
  key: string;
  type: 'website' | 'channel' | 'other';
  socialProfileId?: string;
  platform?: string;
  profileId?: string | null;
  name?: string | null;
  username?: string | null;
  active?: boolean;
  cards: BoardCard[];
}

export interface TeamPerson {
  userId: string;
  email: string;
  name: string;
  role: string;
  pending: boolean;
  you: boolean;
}

export interface Board {
  viewer: {
    userId: string;
    role: string;
    canApprove: boolean;
    canInvite: boolean;
    campaignRole: boolean;
  };
  approver: { name: string; email: string } | null;
  siteHost: string | null;
  team: TeamPerson[];
  /** Absent from a server that predates links. */
  links?: CampaignLink[];
  containers: BoardContainer[];
}

const MO = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const DW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());

/** "2026-10-14" → that day, local. */
export function fromYmd(v: string): Date {
  const [y, m, d] = v.split('-').map(Number);
  return new Date(y, m - 1, d);
}

export function toYmd(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/**
 * A card's due day: the one someone set, else its default — dueBase (a post's
 * time, the campaign's start, when it was added) moved by dueOffset days, in
 * the person's own time zone. Null when there's nothing to go on (a draft
 * with no time).
 */
export function dueDay(c: Pick<BoardCard, 'dueDate' | 'dueBase' | 'dueOffset'>): Date | null {
  if (c.dueDate) return fromYmd(c.dueDate);
  if (!c.dueBase) return null;
  const base = new Date(c.dueBase);
  if (Number.isNaN(base.getTime())) return null;
  const d = startOfDay(base);
  d.setDate(d.getDate() + (c.dueOffset || 0));
  return d;
}

/** Whole days from today to the day (negative: overdue). */
export function daysFromToday(d: Date, now = new Date()): number {
  return Math.round((startOfDay(d).getTime() - startOfDay(now).getTime()) / 86400000);
}

export const monthDay = (d: Date) => `${MO[d.getMonth()]} ${d.getDate()}`;

/** "Due Oct 14", "Due today", "Overdue · Oct 11" — and its colour. */
export function dueWords(
  d: Date | null,
  now = new Date(),
  /** The campaign's last day: work due after it says so (backlog 33236a52). */
  end: Date | null = null
): { text: string; ink: string; n: number } {
  if (!d) return { text: 'No due date', ink: 'var(--ink-ink3)', n: Infinity };
  const n = daysFromToday(d, now);
  if (n >= 0 && end && daysFromToday(d, end) > 0) {
    return { n, text: `Due ${monthDay(d)} · after it ends`, ink: 'var(--ink-amber)' };
  }
  return {
    n,
    text: n < 0 ? `Overdue · ${monthDay(d)}` : n === 0 ? 'Due today' : `Due ${monthDay(d)}`,
    ink: n < 0 ? 'var(--ink-red)' : n <= 2 ? 'var(--ink-amber)' : 'var(--ink-ink2)',
  };
}

/** "Due Tue Oct 14", the notes card's footer. */
export const dueFull = (d: Date | null) =>
  d ? `Due ${DW[d.getDay()]} ${monthDay(d)}` : 'No due date';

/** What's still missing: "Needs headline, summary" or "All details in". */
export function needWords(fields: BoardField[]): { text: string; ok: boolean } {
  const miss = fields.filter((f) => !f.ok).map((f) => f.label.toLowerCase());
  return miss.length
    ? { text: `Needs ${miss.join(', ')}`, ok: false }
    : { text: 'All details in', ok: true };
}

/**
 * A landing page the campaign didn't make (round 34, backlog fdfabdf8): an
 * existing page or another site. Its sign-ups don't count toward the
 * campaign, so it's never Done, has no checklist and stays out of the counts.
 */
export const isNotTracked = (c: Pick<BoardCard, 'status'>) => c.status === 'not_tracked';

/** The line under a Not tracked card's name. */
export function notTrackedLine(c: Pick<BoardCard, 'landing'>): string {
  return c.landing?.kind === 'url'
    ? 'Another site: sign-ups there won’t count toward this campaign'
    : 'Existing page: sign-ups here won’t count toward this campaign';
}

/**
 * A card's status in words. Waiting on approval says who: "To approve"
 * for someone who approves, "Waiting on Hai" for a Campaign-role person.
 */
export function statusWords(
  status: string,
  approverFirst: string | null,
  canApprove: boolean
): { label: string; dot: string; ink: string } {
  const waiting = canApprove ? TO_APPROVE : `Waiting on ${approverFirst || 'the owner'}`;
  const amber = { dot: 'var(--ink-amber)', ink: 'var(--ink-amber)' };
  const green = { dot: 'var(--ink-green)', ink: 'var(--ink-green-ink)' };
  const accent = { dot: 'var(--ink-accent)', ink: 'var(--ink-accent)' };
  const grey = { dot: 'var(--ink-line3)', ink: 'var(--ink-ink3)' };
  const red = { dot: 'var(--ink-red)', ink: 'var(--ink-red)' };
  switch (status) {
    case 'waiting':
      return { label: waiting, ...amber };
    case 'approved':
      return { label: 'Approved', ...green };
    case 'live':
      return { label: 'Live', ...green };
    case 'posted':
      return { label: POST_STATUS.posted, ...green };
    case 'publishing':
      return { label: POST_STATUS.publishing, ...green };
    case 'scheduled':
      return { label: POST_STATUS.scheduled, ...accent };
    case 'missed':
      return { label: POST_STATUS.missed, ...red };
    case 'skipped':
      return { label: POST_STATUS.skipped, ...grey };
    case 'ready':
      return { label: 'Ready for review', ...green };
    case 'in_progress':
      return { label: 'In progress', ...accent };
    case 'done':
      return { label: 'Done', ...green };
    case 'todo':
      return { label: 'To do', ...grey };
    case 'not_tracked':
      return { label: 'Not tracked', ...amber };
    case 'elsewhere':
      return { label: 'Another address', ...grey };
    case 'not_started':
      return { label: 'Not started', ...grey };
    case 'draft':
    default:
      return { label: 'Draft', ...grey };
  }
}

/**
 * "a" or "an" for a channel name, by how it's said: "an X post" (ex), "an
 * Instagram post", "a LinkedIn post", "a YouTube post". A name said letter by
 * letter (X, or an all-caps short name) takes "an" when its first letter
 * sounds like a vowel (F, H, L, M, N, R, S, X, and A E I O).
 */
export function aOrAn(name: string): 'a' | 'an' {
  const n = name.trim();
  if (!n) return 'a';
  if (n.length <= 4 && n === n.toUpperCase() && /^[A-Z]/.test(n)) {
    return 'AEFHILMNORSX'.includes(n[0]) ? 'an' : 'a';
  }
  if (/^u(ni|s|t)/i.test(n)) return 'a';
  return /^[aeiou]/i.test(n) ? 'an' : 'a';
}

/** "Hai Tran" → "Hai". */
export const firstName = (name?: string | null) => (name || '').trim().split(/\s+/)[0] || null;

/** A dated campaign as the calendar and analytics list it (campaignList / campaignAnalytics). */
export interface CampaignSummary {
  id: string;
  name: string;
  status: string;
  deadline?: string | null;
  startDate?: string | null;
  createdAt?: string | null;
  /** The campaign's color key (campaignColors.ts); null → spread by id. */
  color?: string | null;
  posts: Array<{ status: string }>;
}

export interface CampaignTone {
  label: string;
  dot: string;
  ink: string;
  /** The calendar bar without a campaign color: fill, border, text. */
  bar: { bg: string; bd: string; ink: string };
  /** How a bar in the campaign's color shows this status (campaignPaint). */
  state: BarState;
}

const HATCH =
  'repeating-linear-gradient(135deg, var(--ink-amber-bg) 0 5px, transparent 5px 10px), var(--ink-amber-bg)';

/**
 * A campaign's state in words and colours (Prototype: Campaigns → Calendar
 * and Analytics): waiting on approval (who approves, as on the board),
 * running, scheduled (approved, not started), a draft, or ended — approved or
 * never.
 */
export function campaignTone(
  phase: 'running' | 'upcoming' | 'ended',
  c: CampaignSummary,
  {
    waiting,
    canApprove,
    approver,
  }: { waiting: boolean; canApprove: boolean; approver: string | null },
  neverApproved: boolean
): CampaignTone {
  const accent = { bg: 'var(--ink-accent)', bd: 'var(--ink-accent)', ink: 'var(--ink-inv-ink)' };
  if (phase === 'ended') {
    return neverApproved
      ? {
          label: 'Never approved',
          dot: 'var(--ink-line3)',
          ink: 'var(--ink-ink3)',
          bar: { bg: 'transparent', bd: 'var(--ink-line3)', ink: 'var(--ink-ink3)' },
          state: 'expired',
        }
      : {
          label: 'Ended',
          dot: 'var(--ink-line3)',
          ink: 'var(--ink-ink3)',
          bar: { bg: 'var(--ink-bg3)', bd: 'var(--ink-line3)', ink: 'var(--ink-ink2)' },
          state: 'ended',
        };
  }
  if (waiting) {
    return {
      label: canApprove ? TO_APPROVE : `Waiting on ${approver || 'the owner'}`,
      dot: 'var(--ink-amber)',
      ink: 'var(--ink-amber)',
      bar: { bg: HATCH, bd: 'var(--ink-amber)', ink: 'var(--ink-amber)' },
      state: 'waiting',
    };
  }
  if (phase === 'running') {
    return {
      label: 'Running',
      dot: 'var(--ink-green)',
      ink: 'var(--ink-green-ink)',
      bar: accent,
      state: 'solid',
    };
  }
  if (c.status === 'active') {
    return {
      label: 'Scheduled',
      dot: 'var(--ink-accent)',
      ink: 'var(--ink-accent)',
      bar: accent,
      state: 'solid',
    };
  }
  return {
    label: 'Draft',
    dot: 'var(--ink-line3)',
    ink: 'var(--ink-ink3)',
    bar: { bg: 'var(--ink-bg3)', bd: 'var(--ink-line3)', ink: 'var(--ink-ink2)' },
    state: 'draft',
  };
}

/** 286000 → "286K", 3820 → "3,820". */
export const compactCount = (n: number | null | undefined) =>
  n == null
    ? '—'
    : n >= 10000
      ? `${(n / 1000).toFixed(n >= 100000 ? 0 : 1).replace(/\.0$/, '')}K`
      : n.toLocaleString('en-US');

/**
 * A column's cards in due-date order (the Prototype's Work columns; feedback
 * 19a5ee63): soonest first, cards with no due date last, and cards due the
 * same day in the order the server gave (when they were added).
 */
export function byDue<T extends Pick<BoardCard, 'dueDate' | 'dueBase' | 'dueOffset'>>(
  cards: T[]
): T[] {
  const at = (c: T) => dueDay(c)?.getTime() ?? Infinity;
  return cards
    .map((card, i) => ({ card, i, t: at(card) }))
    .sort((a, b) => (a.t === b.t ? a.i - b.i : a.t - b.t))
    .map((x) => x.card);
}

// A post past going out: an expired connection no longer matters to it.
const POST_FINISHED = new Set(['posted', 'publishing', 'skipped']);

/** A post that can't go out while its channel's connection is expired (backlog 33999dec). */
export const cantPost = (c: Pick<BoardCard, 'type' | 'done' | 'status'>) =>
  (c.type === 'post' || c.type === 'plan') && !c.done && !POST_FINISHED.has(c.status);

/**
 * "Your Facebook connection expired. Reconnect it before Nov 9 or its 1 post
 * won't go out."; "" when no post waits on an expired connection. `lostPosts`
 * is the channel of each such post; no startDay once the start has passed.
 */
export function lostChannelWords(lostPosts: string[], startDay: string | null = null): string {
  const n = lostPosts.length;
  if (!n) return '';
  const names = [...new Set(lostPosts)];
  const many = names.length > 1;
  return (
    `Your ${andList(names)} ` +
    `connection${many ? 's' : ''} expired. Reconnect ${many ? 'them' : 'it'} ` +
    `${startDay ? `before ${startDay}` : 'first'} or ${many ? 'their' : 'its'} ` +
    `${n} ${n === 1 ? 'post' : 'posts'} won’t go out.`
  );
}

/**
 * The draft campaign banner (feedback 3cc16e85): who drafted it, then what
 * Schedule campaign does — the landing page going live (or that it already
 * is), the posts going out as planned — and that any post can still be
 * skipped. Not "Then …" after the landing page sentence: the posts don't
 * follow from the page being live.
 */
export function draftCampaignWords({
  byAgent,
  startDay,
  landing,
  posts,
  lostPosts = [],
}: {
  byAgent: boolean;
  startDay: string;
  /** null: no landing page. */
  landing: { live: boolean; name: string } | null;
  /** The channel of each post still to go out. */
  posts: string[];
  /** The channel of each post that can't, its connection expired: never promised. */
  lostPosts?: string[];
}): { title: string; note: string } {
  const n = posts.length;
  const channels = [...new Set(posts)];
  const what =
    n === 1
      ? `the 1 ${channels[0]} post`
      : `all ${n} ${channels.length === 1 ? `${channels[0]} ` : ''}posts`;
  const live = landing?.live
    ? `your landing page${landing.name ? ` (${landing.name})` : ''} is already live`
    : '';
  const does = [
    landing && !landing.live ? `puts the landing page live on ${startDay}` : '',
    n ? `sends ${what} as planned` : '',
  ].filter(Boolean);
  const liveAlone = live ? `${live[0].toUpperCase()}${live.slice(1)}.` : '';
  const first = does.length
    ? `Scheduling it ${does.join(' and ')}${live ? `; ${live}` : ''}.`
    : lostPosts.length
      ? liveAlone
      : `No posts are planned yet, so scheduling only sets it to start ${startDay}.${
          live ? ` ${liveAlone}` : ''
        }`;
  return {
    title: byAgent ? 'Your agent drafted this campaign' : 'This campaign is a draft',
    note: [
      first,
      lostChannelWords(lostPosts, startDay),
      n || lostPosts.length ? 'You can still skip any post on the calendar.' : '',
    ]
      .filter(Boolean)
      .join(' '),
  };
}
